import { useState, useEffect, useCallback } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, Package, Globe, Clock, XCircle, Users } from 'lucide-react';
import { useAuth } from '../hooks/useAuth.js';
import {
  listCatalogs,
  createCatalog,
  updateCatalog,
  deleteCatalog,
  getMyPublished,
  publishCatalogToMarketplace,
  requestMarketplaceListing,
  unpublishCatalogFromMarketplace,
  unsubscribeFromCatalog,
  listMySubscriptions,
  ApiError,
} from '../api/client.js';
import type { Catalog, PublishedCatalog, CatalogSubscription } from '../api/client.js';

export function CatalogsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isManufacturer = user?.tenantType === 'manufacturer';

  const [catalogs, setCatalogs] = useState<Catalog[]>([]);
  const [publishedMap, setPublishedMap] = useState<Record<string, PublishedCatalog>>({});
  const [subscriptions, setSubscriptions] = useState<CatalogSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingCatalog, setEditingCatalog] = useState<Catalog | null>(null);
  const [modalName, setModalName] = useState('');
  const [modalDescription, setModalDescription] = useState('');
  const [modalActive, setModalActive] = useState(true);
  const [modalSaving, setModalSaving] = useState(false);
  const [modalError, setModalError] = useState('');

  // Discount editing state (reseller subscribed catalogs)
  const [editDiscountId, setEditDiscountId] = useState<string | null>(null);
  const [editDiscountValue, setEditDiscountValue] = useState('');
  const [discountSaving, setDiscountSaving] = useState(false);

  // Delete state
  const [deletingCatalog, setDeletingCatalog] = useState<Catalog | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Publish state
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [confirmRequestListing, setConfirmRequestListing] = useState<string | null>(null);
  const [confirmUnpublish, setConfirmUnpublish] = useState<string | null>(null);
  const [confirmUnsub, setConfirmUnsub] = useState<Catalog | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 4000);
  };

  const loadCatalogs = useCallback(async () => {
    try {
      const [catalogData, subData] = await Promise.all([
        listCatalogs(),
        listMySubscriptions(),
      ]);
      setCatalogs(catalogData.catalogs);
      setSubscriptions(subData.subscriptions);

      if (isManufacturer) {
        try {
          const pubData = await getMyPublished();
          const map: Record<string, PublishedCatalog> = {};
          for (const p of pubData.published) {
            map[p.catalogId] = p;
          }
          setPublishedMap(map);
        } catch {
          // Non-critical
        }
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load catalogs');
    } finally {
      setLoading(false);
    }
  }, [isManufacturer]);

  useEffect(() => {
    loadCatalogs();
  }, [loadCatalogs]);

  const openCreate = () => {
    setEditingCatalog(null);
    setModalName('');
    setModalDescription('');
    setModalActive(true);
    setModalError('');
    setShowModal(true);
  };

  const openEdit = (catalog: Catalog) => {
    setEditingCatalog(catalog);
    setModalName(catalog.name);
    setModalDescription(catalog.description ?? '');
    setModalActive(catalog.active);
    setModalError('');
    setShowModal(true);
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    setModalSaving(true);
    setModalError('');

    try {
      if (editingCatalog) {
        await updateCatalog(editingCatalog.id, {
          name: modalName,
          description: modalDescription || undefined,
          active: modalActive,
        });
      } else {
        await createCatalog({
          name: modalName,
          description: modalDescription || undefined,
        });
      }
      setShowModal(false);
      await loadCatalogs();
    } catch (err) {
      setModalError(err instanceof ApiError ? err.message : 'Failed to save catalog');
    } finally {
      setModalSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingCatalog) return;
    setDeleteLoading(true);

    try {
      await deleteCatalog(deletingCatalog.id);
      setDeletingCatalog(null);
      await loadCatalogs();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to delete catalog');
      setDeletingCatalog(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSaveDiscount = async (catalogId: string) => {
    setDiscountSaving(true);
    try {
      const pct = parseFloat(editDiscountValue) || 0;
      await updateCatalog(catalogId, { resellerDiscount: (pct / 100).toString() } as Partial<Catalog>);
      setEditDiscountId(null);
      showToast('Discount updated');
      await loadCatalogs();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to update discount');
    } finally {
      setDiscountSaving(false);
    }
  };

  const handlePublish = async (catalogId: string) => {
    setPublishingId(catalogId);
    try {
      await publishCatalogToMarketplace(catalogId);
      showToast('Catalog published successfully');
      await loadCatalogs();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to publish');
    } finally {
      setPublishingId(null);
    }
  };

  const handleRequestListing = async () => {
    if (!confirmRequestListing) return;
    setActionLoading(true);
    try {
      await requestMarketplaceListing(confirmRequestListing);
      showToast('Marketplace listing requested. Pending admin approval.');
      setConfirmRequestListing(null);
      await loadCatalogs();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to request listing');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnpublish = async () => {
    if (!confirmUnpublish) return;
    setActionLoading(true);
    try {
      await unpublishCatalogFromMarketplace(confirmUnpublish);
      showToast('Catalog unpublished');
      setConfirmUnpublish(null);
      await loadCatalogs();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to unpublish');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnsubscribe = async () => {
    if (!confirmUnsub) return;
    const sub = subscriptions.find(
      (s) => s.localCatalogId === confirmUnsub.id,
    );
    if (!sub) return;
    setActionLoading(true);
    try {
      await unsubscribeFromCatalog(sub.id);
      showToast('Unsubscribed. Local catalog copy removed.');
      setConfirmUnsub(null);
      await loadCatalogs();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to unsubscribe');
    } finally {
      setActionLoading(false);
    }
  };

  const getSubForCatalog = (catalogId: string) =>
    subscriptions.find((s) => s.localCatalogId === catalogId);

  function renderPublishBadge(catalog: Catalog) {
    if (!isManufacturer || catalog.isSubscribedCopy) return null;

    const pub = publishedMap[catalog.id];
    if (!pub) return null;

    const status = pub.marketplaceStatus;
    if (status === 'published') {
      return (
        <span className="rounded-full bg-blue-900/30 px-2 py-0.5 text-xs text-blue-400">
          Published
        </span>
      );
    }
    if (status === 'pending') {
      return (
        <span className="rounded-full bg-amber-900/30 px-2 py-0.5 text-xs text-amber-400">
          Pending Approval
        </span>
      );
    }
    if (status === 'approved') {
      return (
        <span className="flex items-center gap-1 rounded-full bg-green-900/30 px-2 py-0.5 text-xs text-green-400">
          <Globe className="h-3 w-3" />
          Marketplace
          {(pub.subscriberCount ?? 0) > 0 && (
            <span className="ml-1 flex items-center gap-0.5">
              <Users className="h-3 w-3" />
              {pub.subscriberCount}
            </span>
          )}
        </span>
      );
    }
    if (status === 'rejected') {
      return (
        <span
          className="rounded-full bg-red-900/30 px-2 py-0.5 text-xs text-red-400"
          title={pub.rejectionReason || 'Rejected'}
        >
          Rejected
        </span>
      );
    }
    return null;
  }

  function renderPublishActions(catalog: Catalog) {
    if (!isManufacturer || catalog.isSubscribedCopy) return null;

    const pub = publishedMap[catalog.id];
    if (!pub) {
      return (
        <button
          onClick={() => handlePublish(catalog.id)}
          disabled={publishingId === catalog.id}
          className="rounded px-2 py-1 text-xs text-brand-400 hover:bg-gray-800 disabled:opacity-50"
        >
          {publishingId === catalog.id ? 'Publishing...' : 'Publish'}
        </button>
      );
    }

    const btns: React.ReactNode[] = [];
    if (pub.marketplaceStatus === 'published') {
      btns.push(
        <button
          key="request"
          onClick={() => setConfirmRequestListing(catalog.id)}
          className="rounded px-2 py-1 text-xs text-brand-400 hover:bg-gray-800"
        >
          Request Listing
        </button>,
      );
    }
    btns.push(
      <button
        key="unpub"
        onClick={() => setConfirmUnpublish(catalog.id)}
        className="rounded px-2 py-1 text-xs text-red-400 hover:bg-gray-800"
      >
        Unpublish
      </button>,
    );
    return <>{btns}</>;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-400 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Product Catalogs</h1>
          <p className="mt-1 text-sm text-gray-400">
            Manage your product catalogs and their contents
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
        >
          <Plus className="h-4 w-4" />
          New Catalog
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-900/30 px-4 py-3 text-sm text-red-400">{error}</div>
      )}

      {toast && (
        <div className="mb-4 rounded-lg bg-green-900/30 px-4 py-3 text-sm text-green-400">
          {toast}
        </div>
      )}

      {catalogs.length === 0 ? (
        <div className="rounded-lg border border-gray-800 bg-gray-900 p-12 text-center">
          <Package className="mx-auto h-12 w-12 text-gray-600" />
          <h3 className="mt-4 text-lg font-medium text-gray-300">No catalogs yet</h3>
          <p className="mt-2 text-sm text-gray-500">
            Create your first product catalog to get started.
          </p>
          <button
            onClick={openCreate}
            className="mt-4 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
          >
            Create Catalog
          </button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {catalogs.map((catalog) => {
            const isSub = catalog.isSubscribedCopy;
            const sub = isSub ? getSubForCatalog(catalog.id) : null;

            return (
              <div
                key={catalog.id}
                className="group cursor-pointer rounded-lg border border-gray-800 bg-gray-900 p-6 transition-colors hover:border-gray-700"
                onClick={() => navigate(`/catalogs/${catalog.id}`)}
              >
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate text-lg font-semibold text-gray-100">
                      {catalog.name}
                    </h3>
                    {catalog.description && (
                      <p className="mt-1 line-clamp-2 text-sm text-gray-400">
                        {catalog.description}
                      </p>
                    )}
                  </div>
                  <div className="ml-3 flex flex-shrink-0 flex-col items-end gap-1">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs ${
                        catalog.active
                          ? 'bg-green-900/30 text-green-400'
                          : 'bg-gray-800 text-gray-500'
                      }`}
                    >
                      {catalog.active ? 'Active' : 'Inactive'}
                    </span>
                    {isSub && (
                      <span className="rounded-full bg-purple-900/30 px-2 py-0.5 text-xs text-purple-400">
                        Subscribed
                      </span>
                    )}
                    {renderPublishBadge(catalog)}
                  </div>
                </div>

                {/* Reseller discount field for subscribed catalogs */}
                {isSub && !isManufacturer && (
                  <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                    {editDiscountId === catalog.id ? (
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-gray-500">Your Discount</label>
                        <input
                          type="number"
                          value={editDiscountValue}
                          onChange={(e) => setEditDiscountValue(e.target.value)}
                          className="w-20 rounded border border-gray-700 bg-gray-800 px-2 py-1 text-sm text-gray-100 focus:border-brand-500 focus:outline-none"
                          step="0.1"
                          min="0"
                          max="99.99"
                          autoFocus
                        />
                        <span className="text-sm text-gray-400">%</span>
                        <button
                          onClick={() => handleSaveDiscount(catalog.id)}
                          disabled={discountSaving}
                          className="rounded px-2 py-0.5 text-xs font-medium text-brand-400 hover:bg-gray-800"
                        >
                          {discountSaving ? '...' : 'Save'}
                        </button>
                        <button
                          onClick={() => setEditDiscountId(null)}
                          className="text-xs text-gray-500 hover:text-gray-300"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setEditDiscountId(catalog.id);
                          setEditDiscountValue(
                            catalog.resellerDiscount
                              ? (parseFloat(catalog.resellerDiscount) * 100).toFixed(2)
                              : '0',
                          );
                        }}
                        className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-200"
                      >
                        <span>
                          Your Discount:{' '}
                          <span className="font-medium text-gray-200">
                            {catalog.resellerDiscount
                              ? `${(parseFloat(catalog.resellerDiscount) * 100).toFixed(1)}%`
                              : '0%'}
                          </span>
                        </span>
                        <Pencil className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                )}

                <div
                  className="mt-4 flex items-center justify-between"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span
                    className="cursor-pointer text-sm text-brand-400 group-hover:underline"
                    onClick={() => navigate(`/catalogs/${catalog.id}`)}
                  >
                    View Products &rarr;
                  </span>
                  <div className="flex items-center gap-1">
                    {/* Publish actions for manufacturers */}
                    {renderPublishActions(catalog)}

                    {/* Unsubscribe for subscribed catalogs */}
                    {isSub && sub && (
                      <button
                        onClick={() => setConfirmUnsub(catalog)}
                        className="rounded px-2 py-1 text-xs text-red-400 hover:bg-gray-800"
                      >
                        Unsubscribe
                      </button>
                    )}

                    {/* Edit/delete only for non-subscribed catalogs */}
                    {!isSub && (
                      <>
                        <button
                          onClick={() => openEdit(catalog)}
                          className="rounded p-1.5 text-gray-500 hover:bg-gray-800 hover:text-gray-300"
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setDeletingCatalog(catalog)}
                          className="rounded p-1.5 text-gray-500 hover:bg-gray-800 hover:text-red-400"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-lg border border-gray-800 bg-gray-900 p-6">
            <h2 className="mb-4 text-lg font-semibold text-gray-100">
              {editingCatalog ? 'Edit Catalog' : 'New Catalog'}
            </h2>

            {modalError && (
              <div className="mb-4 rounded-lg bg-red-900/30 px-4 py-3 text-sm text-red-400">
                {modalError}
              </div>
            )}

            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label htmlFor="catalog-name" className="mb-1 block text-sm text-gray-400">
                  Name *
                </label>
                <input
                  id="catalog-name"
                  type="text"
                  value={modalName}
                  onChange={(e) => setModalName(e.target.value)}
                  className="w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-2.5 text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  placeholder="e.g. Switches & Routers"
                  required
                />
              </div>

              <div>
                <label htmlFor="catalog-desc" className="mb-1 block text-sm text-gray-400">
                  Description
                </label>
                <textarea
                  id="catalog-desc"
                  value={modalDescription}
                  onChange={(e) => setModalDescription(e.target.value)}
                  rows={3}
                  className="w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-2.5 text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  placeholder="Optional description..."
                />
              </div>

              {editingCatalog && (
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={modalActive}
                    onChange={(e) => setModalActive(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-700 bg-gray-800 text-brand-600 focus:ring-brand-500"
                  />
                  <span className="text-sm text-gray-300">Active</span>
                </label>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={modalSaving}
                  className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {modalSaving ? 'Saving...' : editingCatalog ? 'Save Changes' : 'Create Catalog'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingCatalog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-sm rounded-lg border border-gray-800 bg-gray-900 p-6">
            <h2 className="mb-2 text-lg font-semibold text-gray-100">Delete Catalog</h2>
            <p className="mb-6 text-sm text-gray-400">
              Delete catalog &ldquo;{deletingCatalog.name}&rdquo;? This will also delete all
              products in this catalog. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeletingCatalog(null)}
                className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteLoading}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request Marketplace Listing Confirmation */}
      {confirmRequestListing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-sm rounded-lg border border-gray-800 bg-gray-900 p-6">
            <h2 className="mb-2 text-lg font-semibold text-gray-100">Request Marketplace Listing</h2>
            <p className="mb-6 text-sm text-gray-400">
              This will submit your catalog for review. Once approved, resellers can subscribe to it.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmRequestListing(null)}
                className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleRequestListing}
                disabled={actionLoading}
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {actionLoading ? 'Submitting...' : 'Submit for Review'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unpublish Confirmation */}
      {confirmUnpublish && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-sm rounded-lg border border-gray-800 bg-gray-900 p-6">
            <h2 className="mb-2 text-lg font-semibold text-gray-100">Unpublish Catalog</h2>
            <p className="mb-6 text-sm text-gray-400">
              Existing subscribers will keep their copies but won&apos;t receive future updates.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmUnpublish(null)}
                className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleUnpublish}
                disabled={actionLoading}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading ? 'Unpublishing...' : 'Unpublish'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unsubscribe Confirmation */}
      {confirmUnsub && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-sm rounded-lg border border-gray-800 bg-gray-900 p-6">
            <h2 className="mb-2 text-lg font-semibold text-gray-100">Unsubscribe</h2>
            <p className="mb-6 text-sm text-gray-400">
              Unsubscribe from &ldquo;{confirmUnsub.name}&rdquo;? This will delete the local
              catalog copy and all its products. This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmUnsub(null)}
                className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleUnsubscribe}
                disabled={actionLoading}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading ? 'Removing...' : 'Unsubscribe'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
